import { useState, useEffect } from 'react';
import {
  Box, Typography, Stack, Button, Paper, Chip, Alert,
  IconButton, Tooltip, FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import {
  Add as AddIcon, Edit as EditIcon, Block as BlockIcon,
  Refresh as RefreshIcon, Gavel as GavelIcon,
} from '@mui/icons-material';
import { useDescuentoJudicialStore } from '../store/descuentoJudicialStore';
import type { DescuentoJudicial } from '../types/descuentoJudicial';
import { TIPOS_DESCUENTO, ESTADOS_DESCUENTO } from '../types/descuentoJudicial';
import EditDescuentoJudicial from './EditDescuentoJudicial';

export default function DescuentosJudicialesPage() {
  const { descuentos, loading, error, fetchAll, desactivarDescuento } = useDescuentoJudicialStore();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDescuento, setEditingDescuento] = useState<DescuentoJudicial | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [filtroEstado, setFiltroEstado] = useState<string>('');

  useEffect(() => {
    fetchAll(undefined, filtroEstado || undefined);
  }, [filtroEstado]);

  const handleNew = () => {
    setEditingDescuento(null);
    setDialogOpen(true);
  };

  const handleEdit = (row: DescuentoJudicial) => {
    setEditingDescuento(row);
    setDialogOpen(true);
  };

  const handleDesactivar = async (id: number) => {
    if (!confirm('¿Desactivar este descuento judicial?')) return;
    try {
      await desactivarDescuento(id);
      setMessage('Descuento judicial desactivado');
    } catch { /* handled */ }
  };

  const handleDialogClose = (saved?: boolean) => {
    setDialogOpen(false);
    setEditingDescuento(null);
    if (saved) setMessage('Descuento judicial guardado correctamente');
  };

  const activos = descuentos.filter(d => d.estado === 'ACTIVO');
  const totalPorcentaje = activos.filter(d => d.tipoCalculo === 'PORCENTAJE');
  const totalFijo = activos.filter(d => d.tipoCalculo === 'MONTO_FIJO');

  const columns: GridColDef[] = [
    {
      field: 'acciones', headerName: '', width: 90, sortable: false, filterable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Editar">
            <IconButton size="small" onClick={() => handleEdit(params.row)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {params.row.estado === 'ACTIVO' && (
            <Tooltip title="Desactivar">
              <IconButton size="small" color="warning" onClick={() => handleDesactivar(params.row.idDescuentoJudicial)}>
                <BlockIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
    {
      field: 'trabajadorNombre', headerName: 'Trabajador', flex: 1, minWidth: 200,
      valueGetter: (_v, row) => row.trabajador?.personal?.personaContacto ?? `#${row.idTrabajador}`,
    },
    {
      field: 'tipoDescuento', headerName: 'Tipo', width: 170,
      renderCell: (p) => {
        const t = TIPOS_DESCUENTO.find(td => td.value === p.value);
        return <Chip label={t?.label ?? p.value} size="small" sx={{ bgcolor: t?.color, color: '#fff' }} />;
      },
    },
    {
      field: 'tipoCalculo', headerName: 'Cálculo', width: 130,
      renderCell: (p) => p.value === 'PORCENTAJE'
        ? <Chip label={`${p.row.porcentaje}%`} size="small" color="primary" variant="outlined" />
        : <Chip label={`S/ ${p.row.montoFijo.toFixed(2)}`} size="small" color="secondary" variant="outlined" />,
    },
    { field: 'beneficiario', headerName: 'Beneficiario', flex: 1, minWidth: 180 },
    { field: 'numeroExpediente', headerName: 'N° Expediente', width: 140 },
    { field: 'juzgado', headerName: 'Juzgado', width: 160 },
    {
      field: 'fechaInicio', headerName: 'Desde', width: 110,
      valueFormatter: (v) => v ? new Date(v).toLocaleDateString('es-PE') : '',
    },
    {
      field: 'fechaFin', headerName: 'Hasta', width: 110,
      valueFormatter: (v) => v ? new Date(v).toLocaleDateString('es-PE') : 'Indefinido',
    },
    {
      field: 'estado', headerName: 'Estado', width: 110,
      renderCell: (p) => {
        const e = ESTADOS_DESCUENTO.find(ed => ed.value === p.value);
        return <Chip label={e?.label ?? p.value} size="small" sx={{ bgcolor: e?.color, color: '#fff' }} />;
      },
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <GavelIcon color="primary" />
        <Typography variant="h5" fontWeight={600}>Descuentos Judiciales</Typography>
      </Stack>

      {message && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage(null)}>{message}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Estado</InputLabel>
          <Select value={filtroEstado} label="Estado" onChange={(e) => setFiltroEstado(e.target.value)}>
            <MenuItem value="">Todos</MenuItem>
            {ESTADOS_DESCUENTO.map(e => (
              <MenuItem key={e.value} value={e.value}>{e.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button variant="outlined" startIcon={<RefreshIcon />}
          onClick={() => fetchAll(undefined, filtroEstado || undefined)} disabled={loading}>
          Actualizar
        </Button>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleNew}>
          Nuevo Descuento
        </Button>
      </Stack>

      {/* Resumen cards */}
      <Box sx={{ display: 'flex', gap: 3, mb: 2, flexWrap: 'wrap' }}>
        <Paper sx={{ px: 2, py: 1 }}>
          <Typography variant="caption" color="text.secondary">Total Registros</Typography>
          <Typography variant="h6" fontWeight="bold">{descuentos.length}</Typography>
        </Paper>
        <Paper sx={{ px: 2, py: 1 }}>
          <Typography variant="caption" color="text.secondary">Activos</Typography>
          <Typography variant="h6" fontWeight="bold" color="success.main">{activos.length}</Typography>
        </Paper>
        <Paper sx={{ px: 2, py: 1 }}>
          <Typography variant="caption" color="text.secondary">Por Porcentaje</Typography>
          <Typography variant="h6" fontWeight="bold" color="primary.main">{totalPorcentaje.length}</Typography>
        </Paper>
        <Paper sx={{ px: 2, py: 1 }}>
          <Typography variant="caption" color="text.secondary">Monto Fijo</Typography>
          <Typography variant="h6" fontWeight="bold" color="secondary.main">{totalFijo.length}</Typography>
        </Paper>
      </Box>

      {/* DataGrid */}
      <Paper sx={{ height: 'calc(100vh - 340px)' }}>
        <DataGrid
          rows={descuentos}
          columns={columns}
          getRowId={(row) => row.idDescuentoJudicial}
          loading={loading}
          density="compact"
          disableRowSelectionOnClick
          pageSizeOptions={[25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          onRowDoubleClick={(params) => handleEdit(params.row as DescuentoJudicial)}
          sx={{
            border: 'none',
            '& .MuiDataGrid-columnHeaders': { backgroundColor: '#f8fafc', fontSize: '0.75rem' },
            '& .MuiDataGrid-cell': { fontSize: '0.8rem' },
          }}
        />
      </Paper>

      {/* Dialog */}
      <EditDescuentoJudicial
        open={dialogOpen}
        onClose={handleDialogClose}
        descuento={editingDescuento}
      />
    </Box>
  );
}
